import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";
import { RequestError } from "@/app/queries";

/** Люди и когорты (SP5, A2): режимы all / nocohort / <когорта>, менторы, приглашения. */
export type Person2 = components["schemas"]["Person2"];
export type Cohort = components["schemas"]["Cohort"];
export type MentorLoad = components["schemas"]["MentorLoad"];
export type Invite2 = components["schemas"]["Invite2"];
export type StepFeedback = components["schemas"]["StepFeedback"];
export type PeopleFilter = { scope?: string; role?: Person2["role"]; mentor?: string; q?: string };

function fail(status: number, error: unknown): never {
  const e = error as { error?: string; message?: string } | undefined;
  throw new RequestError(status, e?.error ?? "unknown", e?.message ?? "Не удалось выполнить запрос.");
}

export const peopleKeys = {
  people: (f: PeopleFilter) => ["people", f.scope ?? "all", f.role ?? "", f.mentor ?? "", f.q ?? ""] as const,
  cohorts: ["cohorts"] as const,
  load: (scope: string) => ["mentor-load", scope] as const,
  invites: ["invites"] as const,
  feedback: (stepId: string) => ["step-feedback", stepId] as const,
};

const noRetry4xx = (n: number, err: unknown) => !(err instanceof RequestError && err.status >= 400 && err.status < 500) && n < 2;

export function usePeople(f: PeopleFilter) {
  return useQuery({
    queryKey: peopleKeys.people(f),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/admin/people", { params: { query: { scope: f.scope ?? "all", role: f.role, mentor: f.mentor, q: f.q || undefined } } });
      if (!data) fail(response.status, error);
      return data;
    },
    retry: noRetry4xx,
  });
}

export function useCohorts() {
  return useQuery({
    queryKey: peopleKeys.cohorts,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/admin/cohorts");
      if (!data) fail(response.status, error);
      return data;
    },
    retry: noRetry4xx,
  });
}

export function useMentorLoad(scope = "all") {
  return useQuery({
    queryKey: peopleKeys.load(scope),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/admin/mentor-load", { params: { query: { scope } } });
      if (!data) fail(response.status, error);
      return data;
    },
    retry: noRetry4xx,
  });
}

export function useInvites() {
  return useQuery({
    queryKey: peopleKeys.invites,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/admin/invites/batch");
      if (!data) fail(response.status, error);
      return data;
    },
    retry: noRetry4xx,
  });
}

function invalidatePeople(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ["people"] });
  void qc.invalidateQueries({ queryKey: ["cohorts"] });
  void qc.invalidateQueries({ queryKey: ["mentor-load"] });
  void qc.invalidateQueries({ queryKey: ["invites"] });
}

/** Назначить ментора строкой или пачкой; mentor_id пусто — снять. */
export function useAssignMentor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { students: string[]; mentor_id?: string }) => {
      const { data, error, response } = await api.PUT("/admin/people/mentor", { body });
      if (!data) fail(response.status, error);
      return data;
    },
    onSuccess: () => invalidatePeople(qc),
  });
}

export function useAssignCohort() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { students: string[]; cohort_id?: string }) => {
      const { data, error, response } = await api.PUT("/admin/people/cohort", { body });
      if (!data) fail(response.status, error);
      return data;
    },
    onSuccess: () => invalidatePeople(qc),
  });
}

export function useSetRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: Person2["role"] }) => {
      const { error, response } = await api.PUT("/admin/people/{userId}/role", { params: { path: { userId } }, body: { role } });
      if (!response.ok) fail(response.status, error);
    },
    onSuccess: () => invalidatePeople(qc),
  });
}

export function useCreateCohort() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name: string; status?: Cohort["status"]; starts_on?: string }) => {
      const { data, error, response } = await api.POST("/admin/cohorts", { body });
      if (!data) fail(response.status, error);
      return data;
    },
    onSuccess: () => invalidatePeople(qc),
  });
}

/** Приглашения: email-ы через запятую или текст CSV; mentor_id пусто — поровну. */
export function useInviteBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { emails?: string; csv?: string; role?: "student" | "mentor" | "admin"; cohort_id?: string; mentor_id?: string }) => {
      const { data, error, response } = await api.POST("/admin/invites/batch", { body });
      if (!data) fail(response.status, error);
      return data;
    },
    onSuccess: () => invalidatePeople(qc),
  });
}

/** Отзыв студента на шаг (S4). */
export function useMyStepFeedback(stepId: string) {
  return useQuery({
    queryKey: peopleKeys.feedback(stepId),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/steps/{stepId}/feedback", { params: { path: { stepId } } });
      if (!data) fail(response.status, error);
      return data.feedback ?? null;
    },
    retry: noRetry4xx,
  });
}

export function useSaveStepFeedback(stepId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { rating: number; body?: string }) => {
      const { data, error, response } = await api.PUT("/steps/{stepId}/feedback", { params: { path: { stepId } }, body });
      if (!data) fail(response.status, error);
      return data;
    },
    onSuccess: (f) => qc.setQueryData(peopleKeys.feedback(stepId), f),
  });
}

/** Отзывы на шаг для staff (A1 счётчик, A3): последние и средняя. */
export function useStepFeedbackAdmin(stepId: string, enabled = true) {
  return useQuery({
    queryKey: ["step-feedback", "admin", stepId],
    enabled,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/admin/steps/{stepId}/feedback", { params: { path: { stepId } } });
      if (!data) fail(response.status, error);
      return data;
    },
    retry: noRetry4xx,
  });
}
